import { createContext, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";

import {
  Box,
  Grid2,
  DialogTitle,
  DialogContent,
  Typography,
} from "@mui/material";

import { DownloadTableToExcel } from "components/DownloadTableToExcel";
import { List } from "components/ListPage/List/Row/History/Rental/List";
import { Widgets } from "components/ListPage/List/Row/History/Widgets";
import { CloseX } from "components/utility/CloseX";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { ModalTitle } from "components/utility/ModalTitle";

import { i18n } from "services/i18nService";
import { getItemRecord } from "services/sosInventoryService/domainLogic";
import { TYPE_FILTER_OPTIONS } from "services/sosInventoryService/rentalHistory/schema";
import { getRentalHistory } from "services/sosInventoryService/sosApi";
import { calendarFormat } from "services/utility/dates";

import { useErrors } from "hooks/useErrors";

import { theme } from "SosTheme";
import { ITEM_HISTORY_DEFAULT_FILTERS } from "appConstants";

export const ListPageState = createContext();

const sx = {
  padding: "0.4rem",
  height: "36px",
  border: "1px solid",
  borderColor: "lineSeparatorMinor",
  backgroundColor: "white",
  displayPrint: "none",
};

export function ItemHistory(props) {
  const { setOpen, objectType, id, identifierText } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(null);
  const [retrievalParams, setRetrievalParams] = useState(
    ITEM_HISTORY_DEFAULT_FILTERS
  );

  const tableRef = useRef(null);

  const recordsChanged = useSelector((state) => state.recordsChanged);

  const [history, setHistory] = useState(null);
  const [totalHistoryCount, setTotalHistoryCount] = useState(null);

  const { errors, setErrors } = useErrors();

  // fetches list data
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { start, maxResults, direction, query, sort } = retrievalParams;
      const { fromLocation, toLocation, type, toDate, fromDate } =
        retrievalParams;
      const response = await getRentalHistory(objectType, id, {
        direction,
        maxResults,
        query,
        sort,
        start,
        fromLocation: fromLocation.id ? fromLocation.name : "",
        toLocation: toLocation.id ? toLocation.name : "",
        type: type.id ? type.name : "",
        to: toDate ? calendarFormat(toDate) : "",
        from: fromDate ? calendarFormat(fromDate) : "",
      });
      // response can be undefined, if the get was aborted by further
      // typing in the search field
      if (response) {
        setIsLoading(false);
        setHistory(response.data);
        setTotalHistoryCount(response.totalCount);
      }
    })();
  }, [retrievalParams, id, objectType, recordsChanged]);

  // fetches item quantity value
  useEffect(() => {
    (async () => {
      const { onhand } = await getItemRecord(id, undefined, undefined, [
        "onhand",
      ]);
      setQuantity(onhand);
    })();
  }, [retrievalParams, id, objectType]);

  return (
    <>
      <DialogTitle
        sx={{
          backgroundColor: "dialogTitleBackground",
          borderBottom: "1px solid",
          borderBottomColor: "lineSeparatorMinor",
          padding: "0.5rem 1rem",
        }}
      >
        <Grid2
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid2 size={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box style={{ display: "flex", alignItems: "center" }}>
                <CloseX onClick={() => setOpen(false)} />
                <ModalTitle
                  label={i18n("global.History")}
                  text={identifierText}
                />
              </Box>
              <Box>
                <DownloadTableToExcel
                  tableRef={tableRef}
                  fileName={i18n("download.RentalHistoryDownloadFileName")}
                  iconSx={sx}
                  iconStyle={{
                    borderRadius: theme.shape.borderRadius,
                    color: theme.palette.icons,
                  }}
                />
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </DialogTitle>
      {isLoading && <Loading />}
      <DialogContent>
        <FormErrors errors={errors} setErrors={setErrors} />
        {quantity && (
          <Typography sx={{ marginTop: "1rem" }}>
            <>
              <span style={{ fontWeight: "bold" }}>
                {i18n("colon.CurrentQuantity")}
              </span>
              <span> {quantity.toString()}</span>
            </>
          </Typography>
        )}
        {history ? (
          <>
            <Widgets
              totalHistoryCount={totalHistoryCount}
              retrievalParams={retrievalParams}
              setRetrievalParams={setRetrievalParams}
              typeOptions={TYPE_FILTER_OPTIONS}
            />
            <List
              history={history}
              retrievalParams={retrievalParams}
              setRetrievalParams={setRetrievalParams}
              tableRef={tableRef}
            />
          </>
        ) : (
          <FixedLoadingIndicator text={`${i18n("global.Loading")}...`} />
        )}
      </DialogContent>
    </>
  );
}
